import { Prisma } from '@prisma/client'
import { PaginationProps } from 'antd'
import { useMemo, useState } from 'react'
import { Api } from '~/core/trpc'
import { OrganizationType } from '~/features/organization/common'

export type DataFilters = {
  status: ('OPEN' | 'CLOSED')[]
}

type Props = {
  organization: OrganizationType
}

const PAGE_SIZE = 20

export const useData = ({ organization }: Props) => {
  const [pageIndex, setPageIndex] = useState<number>(1)

  const [filters, setFilters] = useState<DataFilters>({
    status: ['OPEN'],
  })

  const whereConditions = useMemo(() => {
    const AND: Prisma.SupportTicketWhereInput[] = []

    if (filters?.status?.length > 0) {
      AND.push({
        status: {
          in: filters.status,
        },
      })
    }

    return {
      AND: AND.length > 0 ? AND : undefined,
      conversation: {
        organizationId: organization.id,
        AND: [
          {
            participants: {
              some: {
                organizationContactId: {
                  not: null,
                },
              },
            },
          },
          {
            participants: {
              some: {
                organizationMember: {
                  appSession: {
                    app: {
                      key: 'customerSupport',
                    },
                  },
                },
              },
            },
          },
        ],
      },
    } as any
  }, [filters])

  const countSupportTickets = Api.supportTicket.count.useQuery({
    where: whereConditions,
  })

  const supportTicketsQuery = Api.supportTicket.findMany.useQuery(
    {
      where: whereConditions,
      include: {
        conversation: {
          include: {
            participants: {
              include: {
                organizationMember: true,
                organizationContact: true,
              },
            },
            messages: {
              where: {
                participant: {
                  organizationContactId: {
                    not: null,
                  },
                },
              },
              orderBy: {
                createdAt: 'desc',
              },
              take: 1,
            },
          },
        },
      },
      orderBy: {
        createdAt: 'asc',
      },
      take: PAGE_SIZE,
      skip: (pageIndex - 1) * PAGE_SIZE,
    },
    { initialData: [] },
  )

  const pagination = useMemo((): PaginationProps => {
    return {
      current: pageIndex,
      pageSize: PAGE_SIZE,
      total: countSupportTickets.data ?? 0,
    }
  }, [pageIndex, countSupportTickets.data])

  return {
    isLoading: supportTicketsQuery.isLoading,
    tickets: supportTicketsQuery.data,
    filters,
    setFilters,
    pagination,
    setPageIndex,
    refetch: supportTicketsQuery.refetch,
  }
}

export type DataType = ReturnType<typeof useData>['tickets'][0]
